import { useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { useWorkspaceCreationStorageState } from '../hooks/use-workspace-creation-storage-state';

import type { Language } from '@/entities/workspace/types';
import { Button } from '@/shared/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/shadcn/dropdown-menu';
import { cn } from '@/shared/utils/index';

const AVAILABLE_LANGUAGES: Language[] = [
  { code: 'en', name: 'English' },
  { code: 'ko', name: 'Korean' },
];

export function LanguageSelectionStep() {
  const redirectPage = useSearch({
    from: '/workspace/create/language',
    select: (search) => search.redirectPage,
  });
  const navigate = useNavigate();
  const [workspaceData, setWorkspaceData] = useWorkspaceCreationStorageState();
  const [selectedLanguage, setSelectedLanguage] = useState<string>(workspaceData.language || 'en');

  const handleContinue = () => {
    if (!selectedLanguage) return;

    setWorkspaceData((prev) => ({ ...prev, language: selectedLanguage }));

    navigate({ to: '/workspace/create/user-info', search: { redirectPage } });
  };

  const selectedLanguageData = AVAILABLE_LANGUAGES.find((lang) => lang.code === selectedLanguage);

  return (
    <div className="bg-sidebar-background flex min-h-screen justify-center">
      <div className="mt-60 flex min-w-[610px] flex-col gap-6 px-4">
        <div className="flex flex-col gap-1">
          <div className="flex flex-row items-center gap-2 text-3xl">
            <span className="text-foreground font-bold">✨</span>
            <h1 className="text-foreground font-bold">What language do you prefer?</h1>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h2 className="text-foreground text-2xl leading-none font-medium tracking-tight">
            Select language setting for Syncly AI
          </h2>
          <p className="text-muted-foreground text-2xl leading-none font-medium tracking-tight">
            AI will deliver the output in the language of your choice.
          </p>
        </div>

        <div className="w-full rounded-md">
          <div className="flex flex-col justify-center px-0 py-2 pr-2">
            <div className="flex flex-row items-center justify-between">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      'h-10 w-[252px] justify-between text-base font-normal',
                      !selectedLanguageData && 'text-muted-foreground',
                    )}
                  >
                    <span>{selectedLanguageData?.name || 'Select language'}</span>
                    <ChevronDown className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[252px]">
                  {AVAILABLE_LANGUAGES.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => setSelectedLanguage(lang.code)}
                      className="flex items-center justify-between"
                    >
                      {lang.name}
                      {selectedLanguage === lang.code && <Check className="size-4" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                size="icon"
                className="size-10"
                onClick={handleContinue}
                disabled={!selectedLanguage}
              >
                <ArrowRight className="size-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
